import React, { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { PageHeader, Btn, Input, Select, Modal, Card, Badge, EmptyState, fmtMoney, fmtDate } from "@/components/ui-kit";
import { Plus, FileSpreadsheet, AlertTriangle } from "lucide-react";

export default function Collections() {
  const { t } = useLang();
  const [list, setList] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ pharmacy_id: "", order_id: "", amount: "", payment_method: "cash", reference_number: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [c, p, o] = await Promise.all([api.get("/collections"), api.get("/pharmacies"), api.get("/orders")]);
    setList(c.data); setPharmacies(p.data); setOrders(o.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const pharmacy = pharmacies.find(p => p.id === form.pharmacy_id);
      await api.post("/collections", {
        ...form, amount: Number(form.amount), pharmacy_name: pharmacy?.name,
        order_id: form.order_id || null,
      });
      setOpen(false);
      setForm({ pharmacy_id: "", order_id: "", amount: "", payment_method: "cash", reference_number: "", notes: "" });
      await load();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const overduePharmacies = pharmacies.filter(p => p.outstanding_balance > 0).sort((a, b) => b.outstanding_balance - a.outstanding_balance);
  const totalOutstanding = pharmacies.reduce((s, p) => s + (p.outstanding_balance || 0), 0);
  const totalCollected = list.reduce((s, c) => s + c.amount, 0);

  const pharmacyOrders = form.pharmacy_id ? orders.filter(o => o.pharmacy_id === form.pharmacy_id && o.payment_status !== "paid") : [];

  return (
    <div data-testid="collections-page">
      <PageHeader
        title={t("collections")}
        subtitle="Outstanding balances & payment tracking"
        actions={
          <>
            <Btn variant="secondary" onClick={() => window.open(`${API_BASE}/export/collections/excel`)}><FileSpreadsheet className="w-4 h-4"/>Excel</Btn>
            <Btn onClick={() => setOpen(true)} data-testid="add-collection-btn"><Plus className="w-4 h-4"/>Record Payment</Btn>
          </>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-5">
          <div className="overline">Total Outstanding</div>
          <div className="metric-value text-amber-600 mt-1">{fmtMoney(totalOutstanding)} EGP</div>
          <div className="text-xs text-slate-500 mt-1">{overduePharmacies.length} pharmacies</div>
        </Card>
        <Card className="p-5">
          <div className="overline">Total Collected</div>
          <div className="metric-value text-emerald-600 mt-1">{fmtMoney(totalCollected)} EGP</div>
          <div className="text-xs text-slate-500 mt-1">{list.length} payments</div>
        </Card>
        <Card className="p-5">
          <div className="overline">Avg Payment</div>
          <div className="metric-value text-blue-700 mt-1">{fmtMoney(list.length ? totalCollected / list.length : 0)}</div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-600"/>
            <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>Top Outstanding</h3>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {overduePharmacies.slice(0, 10).map(p => (
              <div key={p.id} className="flex items-center justify-between border-b border-slate-100 py-2">
                <div>
                  <div className="font-semibold text-sm">{p.name}</div>
                  <div className="text-xs text-slate-500">{p.city} · {p.classification}</div>
                </div>
                <div className="font-bold text-amber-700">{fmtMoney(p.outstanding_balance)} EGP</div>
              </div>
            ))}
            {overduePharmacies.length === 0 && <EmptyState message="All paid up!"/>}
          </div>
        </Card>

        <Card>
          <div className="px-5 pt-5 pb-3 border-b border-slate-100">
            <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>Recent Collections</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2 px-3 text-start">Pharmacy</th>
                  <th className="py-2 px-3 text-start">Method</th>
                  <th className="py-2 px-3 text-end">Amount</th>
                  <th className="py-2 px-3 text-start">Date</th>
                </tr>
              </thead>
              <tbody>
                {list.slice(0, 10).map(c => (
                  <tr key={c.id} className="border-b border-slate-100">
                    <td className="py-2 px-3">{c.pharmacy_name}</td>
                    <td className="py-2 px-3"><Badge color="slate">{c.payment_method}</Badge></td>
                    <td className="py-2 px-3 text-end font-bold text-emerald-700">{fmtMoney(c.amount)}</td>
                    <td className="py-2 px-3 text-xs">{fmtDate(c.collection_date || c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {list.length === 0 && <EmptyState message={t("noData")}/>}
          </div>
        </Card>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Record Payment">
        <form onSubmit={submit} className="space-y-3" data-testid="collection-form">
          <Select label="Pharmacy *" required value={form.pharmacy_id} onChange={(e) => setForm({...form, pharmacy_id: e.target.value, order_id: ""})} data-testid="collection-pharmacy">
            <option value="">--</option>
            {pharmacies.map(p => <option key={p.id} value={p.id}>{p.name} (Outstanding: {fmtMoney(p.outstanding_balance)})</option>)}
          </Select>
          <Select label="Link to Order (optional)" value={form.order_id} onChange={(e) => setForm({...form, order_id: e.target.value})}>
            <option value="">--</option>
            {pharmacyOrders.map(o => <option key={o.id} value={o.id}>{o.invoice_number} ({fmtMoney(o.total - o.paid_amount)} due)</option>)}
          </Select>
          <Input label="Amount (EGP) *" type="number" required value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} data-testid="collection-amount"/>
          <Select label="Payment Method *" value={form.payment_method} onChange={(e) => setForm({...form, payment_method: e.target.value})}>
            <option value="cash">Cash</option><option value="bank_transfer">Bank Transfer</option><option value="instapay">InstaPay</option><option value="check">Check</option>
          </Select>
          <Input label="Reference Number" value={form.reference_number} onChange={(e) => setForm({...form, reference_number: e.target.value})}/>
          <Input label="Notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}/>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <Btn variant="ghost" type="button" onClick={() => setOpen(false)}>{t("cancel")}</Btn>
            <Btn type="submit" disabled={busy} data-testid="collection-submit">{busy ? t("loading") : t("save")}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
